import PropTypes from 'prop-types'
import React from 'react'
import ReactCSSTransitionGroup from 'react-addons-css-transition-group'
import classNames from 'classnames'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'

import FirstChild from 'components/FirstChild/FirstChild.jsx'
import ModernizationOverlay from 'components/ModernizationOverlay/ModernizationOverlay.jsx'
import ReserveTooltip from 'components/ReserveTooltip/ReserveTooltip.jsx'
import UpgradeEffect from 'components/UpgradeEffect/UpgradeEffect.jsx'
import NavigationArrow from 'components/NavigationArrow/NavigationArrow.jsx'

import {
    CardImage,
    UpgradeConfirmation,
    Reward,
    UpgradeTooltip,
    UpgradeEffectContent,
} from './components'

import { toRoman } from 'helpers'
import { COMMAND_CENTER_TITLE } from 'helpers/i18n.jsx'

import { playHoverSound, playClickSound } from 'state/actions/sounds'

import {
    COMMAND_CENTER_UPGRADE_TOOLTIP_ID,
    COMMAND_CENTER_MODERNIZATION_PLAN_DESCRIPTION,
    COMMAND_CENTER_REWARDS_TITLE,
    RESERVE_TYPES_ORDER,
    MINIMUM_POSSIBLE_RESERVE_LEVEL,
} from './constants.jsx'

import { MAX_BUILDING_LEVEL, MAX_RESERVE_LEVEL, KEY_CODES } from 'constants'

import { BUILDING, STRONGHOLD } from 'urls'

import styles from './CommandCenterCard.scss'

export class CommandCenterCard extends React.Component {
    static propTypes = {
        level: PropTypes.number.isRequired,
        upgrade: PropTypes.shape({
            confirmationOpened: PropTypes.bool.isRequired,
            submitting: PropTypes.bool.isRequired,
            highlighted: PropTypes.bool.isRequired,
        }).isRequired,
        nextBuildingId: PropTypes.number,
        rewardsByLevel: PropTypes.object,
        confirmationOpened: PropTypes.bool.isRequired,

        playHoverSound: PropTypes.func.isRequired,
        playClickSound: PropTypes.func.isRequired,
        pushState: PropTypes.func.isRequired,
    }
    constructor(props) {
        super(props)
        this.backHandler = props.pushState.bind(null, STRONGHOLD)
        this.onKeyUp = this.onKeyUp.bind(this)
    }
    onKeyUp(e) {
        if (e.keyCode !== KEY_CODES.ESCAPE) return

        const confirmationsOpened = (
            this.props.confirmationOpened || this.refs.overlay.state.opened
        )
        if (!confirmationsOpened) this.backHandler()
    }
    componentWillMount() {
        window.addEventListener('keydown', this.onKeyUp)
    }
    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyUp)
    }
    handleUpgradeEffectClick() {
        this.props.playClickSound()
        this.refs.overlay.show()
    }
    render() {
        const { highlighted } = this.props.upgrade
        const level = (highlighted ? this.props.level + 1 : this.props.level)
        const nextLevel = (
            <span className={styles.planLevel}>
                {toRoman(this.props.level + 1)}
            </span>
        )

        const planLevelClassName = classNames(styles.planLevel, {
            [styles.planLevel__highlighted] : highlighted,
        })

        const planLevel = (
            <span className={planLevelClassName}>
                {toRoman(level)}
            </span>
        )

        const overlayClassName = classNames(styles.overlay, {
            [styles.overlay__enabled]: highlighted,
        })

        const arrowLeftClassName = classNames(styles.arrow, styles.arrow__left)
        const arrowRightClassName = classNames(styles.arrow, styles.arrow__right)

        let handleClickNext = null
        let nextArrowDisabled = true
        if (this.props.nextBuildingId !== undefined) {
            handleClickNext = this.props.pushState.bind(null, BUILDING(this.props.nextBuildingId))
            nextArrowDisabled = false
        }

        const rewards = []
        const tooltips = []
        for (let level = MINIMUM_POSSIBLE_RESERVE_LEVEL; level <= MAX_RESERVE_LEVEL; level++) {
            if (level in this.props.rewardsByLevel) {
                const reward = this.props.rewardsByLevel[level]
                rewards.push(
                    <Reward
                        key={`reward_${level}`}
                        reward={reward}
                    />
                )
                reward.reserves.forEach(reserve => {
                    const tooltipId = `${reserve.type}_${level}_reserve_tooltip_id`
                    tooltips.push(
                        <ReserveTooltip
                            key={tooltipId}
                            id={tooltipId}
                            level={level}
                            type={reserve.type}
                            classNames={{
                                info: styles.tooltipInfo,
                            }}
                        />
                    )
                })
            }
        }

        const rewardsClassName = classNames(styles.rewards, {
            [styles.rewards__highlighted] : highlighted,
        })

        const upgradePlanComponent = this.props.level < MAX_BUILDING_LEVEL ? (
            <div className={styles.cell}>
                <p className={styles.modernizationDescription}>
                    {COMMAND_CENTER_MODERNIZATION_PLAN_DESCRIPTION(planLevel)}
                </p>

                <div className={rewardsClassName}>
                    <h3 className={styles.heading}>
                        {COMMAND_CENTER_REWARDS_TITLE(nextLevel)}
                    </h3>
                    <div className={styles.rewardsDescription}>
                        {rewards}
                    </div>
                </div>
                {tooltips}
            </div>
        ) : null

        return (
            <div className={styles.base}>
                <div className={overlayClassName} />
                <div className={styles.header}>
                    <h2 className={styles.title}>
                        <span className={styles.level}>
                            {toRoman(level)}
                            <span className={styles.levelShadow}>{toRoman(level)}</span>
                        </span>
                        {COMMAND_CENTER_TITLE}
                    </h2>
                </div>
                <div className={styles.main}>
                    <div className={styles.wrapper}>
                        <div className={styles.holder}>
                            <div className={styles.grid}>
                                <div className={styles.cell}>
                                    <CardImage
                                        tooltipId={COMMAND_CENTER_UPGRADE_TOOLTIP_ID}
                                        isMaxCommandCenter={this.props.level === MAX_BUILDING_LEVEL}
                                    />
                                    <UpgradeEffect
                                        handleClick={this.handleUpgradeEffectClick.bind(this)}
                                        handleHover={this.props.playHoverSound}
                                        hasPermission
                                        fade={this.props.upgrade.highlighted}
                                    />
                                </div>
                                {upgradePlanComponent}
                            </div>
                        </div>

                        <div className={arrowLeftClassName}>
                            <NavigationArrow
                                size={NavigationArrow.SIZES.big}
                                type={NavigationArrow.TYPES.left}
                                handleClick={null}
                                disabled
                            />
                        </div>
                        <div className={arrowRightClassName}>
                            <NavigationArrow
                                size={NavigationArrow.SIZES.big}
                                type={NavigationArrow.TYPES.right}
                                handleClick={handleClickNext}
                                disabled={nextArrowDisabled}
                            />
                        </div>
                    </div>
                </div>
                {level < MAX_BUILDING_LEVEL && (
                    <UpgradeTooltip id={COMMAND_CENTER_UPGRADE_TOOLTIP_ID} />
                )}

                <ReactCSSTransitionGroup
                    component={FirstChild}
                    transitionName='upgradeConfirmation'
                    transitionEnterTimeout={200}
                    transitionLeaveTimeout={200}
                >
                    {this.props.upgrade.confirmationOpened ? (
                        <UpgradeConfirmation />
                    ) : null}
                </ReactCSSTransitionGroup>

                <ModernizationOverlay ref='overlay'>
                    <UpgradeEffectContent />
                </ModernizationOverlay>
            </div>
        )
    }
}

export const mapStateToProps = ({ commandCenterUpgrade, buildings, stronghold, settings }) => {
    const level = stronghold.level
    const { confirmationOpened, highlighted, submitting } = commandCenterUpgrade
    const buildingIds = Object.keys(buildings)

    const rewardsByLevel = {}
    if (level < MAX_BUILDING_LEVEL) {
        const commandCenterUpgradeReward = settings.command_center.upgrade_rewards[level + 1]
        for (let reserveType of RESERVE_TYPES_ORDER) {
            const reserveSettings = commandCenterUpgradeReward[reserveType]

            for (let { level, count } of reserveSettings) {
                if (!(level in rewardsByLevel)) {
                    rewardsByLevel[level] = {
                        rewardLevel: level,
                        reserves: [],
                    }
                }

                rewardsByLevel[level].reserves.push({
                    type: reserveType,
                    count,
                })
            }
        }
    }

    return {
        level,
        upgrade: { confirmationOpened, highlighted, submitting },
        nextBuildingId: buildingIds.length > 0 ? Number(buildingIds[0]) : undefined,
        rewardsByLevel,
        confirmationOpened,
    }
}

export const mapDispatchToProps = {
    playHoverSound,
    playClickSound,
    pushState: push,
}

export default connect(mapStateToProps, mapDispatchToProps)(CommandCenterCard)



// WEBPACK FOOTER //
// ./containers/CommandCenterCard/CommandCenterCard.jsx